import { AlertCircle, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { OrganisationTimesheetSettings } from '@/types/timesheetSettings';
import { useDeadlineWarning } from './useDeadlineWarning';
import type { StatusBannerVariant } from './RejectionBanner';

export interface DeadlineWarningBannerProps {
  variant: StatusBannerVariant;
  organizationSettings: OrganisationTimesheetSettings | undefined;
  currentWeekStart: Date;
  status: string;
}

/** Submission deadline approaching / overdue warning for the current week. */
export function DeadlineWarningBanner({
  variant,
  organizationSettings,
  currentWeekStart,
  status,
}: DeadlineWarningBannerProps) {
  const deadlineWarning = useDeadlineWarning(organizationSettings, currentWeekStart, status);

  if (!deadlineWarning) return null;

  const isOverdue = deadlineWarning.type === 'overdue';

  if (variant === 'desktop') {
    return (
      <div className={cn(
        'flex items-start gap-2 border rounded px-2.5 py-1.5',
        isOverdue
          ? 'border-red-400 bg-red-50 dark:bg-red-950/20'
          : 'border-amber-400 bg-amber-50 dark:bg-amber-950/20'
      )}>
        {isOverdue
          ? <AlertCircle className="h-3 w-3 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          : <Clock className="h-3 w-3 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
        }
        <div className="flex flex-col min-w-0">
          <span className={cn('font-semibold leading-tight', isOverdue ? 'text-red-900 dark:text-red-100' : 'text-amber-900 dark:text-amber-100')} style={{ fontSize: '12px' }}>
            {isOverdue ? 'Submission Overdue' : 'Submission Deadline Approaching'}
          </span>
          <span className={cn('leading-tight', isOverdue ? 'text-red-800 dark:text-red-200' : 'text-amber-800 dark:text-amber-200')} style={{ fontSize: '12px' }}>
            {deadlineWarning.message}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={cn(
      'flex items-center gap-1.5 border rounded px-2 py-1',
      variant === 'tablet' && 'min-w-0',
      isOverdue
        ? 'border-red-400 bg-red-50 dark:bg-red-950/20'
        : 'border-amber-400 bg-amber-50 dark:bg-amber-950/20'
    )}>
      {isOverdue
        ? <AlertCircle className="h-3 w-3 text-red-600 dark:text-red-400 flex-shrink-0" />
        : <Clock className="h-3 w-3 text-amber-600 dark:text-amber-400 flex-shrink-0" />
      }
      <span className={cn('font-semibold flex-shrink-0', isOverdue ? 'text-red-900 dark:text-red-100' : 'text-amber-900 dark:text-amber-100')} style={{ fontSize: '12px' }}>
        {isOverdue ? 'Overdue' : 'Deadline Approaching'}
      </span>
      <span className={cn('truncate', isOverdue ? 'text-red-800 dark:text-red-200' : 'text-amber-800 dark:text-amber-200')} style={{ fontSize: '12px' }}>
        · {deadlineWarning.message}
      </span>
    </div>
  );
}
